import { isNaN, toNumber } from 'lodash';
import { PaymentNumpadValue } from '../../payment/payment-numpad.component';
import { NumpadValue } from './numpad.component';

export function convertKeyBoardEventDataToNumpadValue(value: string | number | null): NumpadValue | null {
  const allowedKeyboardInputStringData = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
  const allowedKeyboardInputNumericData = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9];

  if (allowedKeyboardInputStringData.some(v => v === value)) {
    return toNumber(value) as NumpadValue;
  }

  if (allowedKeyboardInputNumericData.some(v => v === value)) {
    return value as NumpadValue;
  }

  // in input backspace and delete returns null, so we convert it
  if (value === null) {
    return 'clear';
  }

  return null;
}

/**
 * Updates currency input's value from numpad event
 *
 * @example
 *  Append value with amount from numpad, but keeps decimal separator on fixed place:
 *  - 5.125 + press number 6 on numpad = 51.256
 *  - 5.125 + press '00' on numpad = 512.500
 *
 *  Add fixed value to amount:
 *  - 5.125 + '500.00' = 505.125
 *
 *  Clear last character from value, but keeps decimal separator on fixed plac:
 *  - 5.125 + press 'clear' on numpad = 0.512
 */
export function updateCurrencyAmount(value: number | string, numpadValue: PaymentNumpadValue | NumpadValue | null, fractionDigits = 2): number {
  /** Converts number to raw numeric string without decimal separator */
  const toRawNumberString = (value: number | string | null): string => {
    const NO_VALUE = 0;
    if (typeof value === 'string') {
      return toNumber(value).toFixed(fractionDigits).replace('.', '');
    }

    if (value === null) {
      return NO_VALUE.toFixed(fractionDigits).replace('.', '');
    }

    return value.toFixed(fractionDigits).replace('.', '');
  };

  /** Converts numeric string back to decimal number */
  const fromRawNumberString = (numericString: string): number => {
    const whole = toNumber(numericString.slice(0, -fractionDigits) || 0);
    const decimal = toNumber(numericString.slice(-fractionDigits)) / (10 ** fractionDigits);
    return whole + decimal;
  };

  switch (numpadValue) {
    case null:
      return fromRawNumberString(toRawNumberString(value));
    case 0:
    case '00':
      if (toNumber(value) === 0 || isNaN(toNumber(value))) {
        return 0;
      }
      return fromRawNumberString(toRawNumberString(value) + numpadValue.toString());
    case '5.00':
    case '10.00':
    case '20.00':
    case '50.00':
    case '100.00':
    case '200.00':
    case '500.00':
      return fromRawNumberString(toRawNumberString(toNumber(value) + toNumber(numpadValue)));
    case 1:
    case 2:
    case 3:
    case 4:
    case 5:
    case 6:
    case 7:
    case 8:
    case 9:
      return fromRawNumberString(toRawNumberString(value) + numpadValue.toString());
    case 'clear':
      return fromRawNumberString(toRawNumberString(value).slice(0, -1));
    default:
      throw new Error(`Unhandled numpad button: ${numpadValue}`);
  }
}
